import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { usePremisesList } from "../hooks/usePremisesList";
import { useAuth, useCan } from "../auth/AuthContext";
import { useScope } from "../context/ScopeContext";
import {
  premiseTypeChips,
  productsForPremise,
  type ProductKey,
} from "../nav/products";
import { vendorFor } from "../utils/devicePresentation";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import HealthChip from "../components/HealthChip";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { isDemoCompanyName } from "@shared/demo";

export default function PremisesPage() {
  const { premises, loading, error, refresh } = usePremisesList();
  const navigate = useNavigate();
  // Creating a premise is Nenda-staff provisioning (ruled 2026-09-22) - the
  // server refuses anyone but a platform admin. Deleting is still the broader
  // layout permission, as before.
  const { user } = useAuth();
  const canCreate = user?.type === "admin";
  const canDelete = useCan("canEditLayout");
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  // Type-column sort: null = unsorted (default order), else asc/desc by segment.
  const [typeOrder, setTypeOrder] = useState<"asc" | "desc" | null>(null);
  const [deleting, setDeleting] = useState<PremiseSummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: "success" | "error";
  } | null>(null);

  // The customer/location narrowing lives in the header scope bar. A
  // ?company= deep-link from the Companies list is folded into it on arrival,
  // so there is only ever one filter in force. "unassigned" is the exception:
  // a synthetic value (premises with no Company) the scope bar cannot express.
  const { scope, setScope } = useScope();
  const unassignedOnly = searchParams.get("company") === "unassigned";

  useEffect(() => {
    foyerApi
      .listCompanies()
      .then(setCompanies)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const deepLink = searchParams.get("company");
    if (!deepLink || deepLink === "unassigned") return;
    setScope({ companyId: deepLink });
    const next = new URLSearchParams(searchParams);
    next.delete("company");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setScope]);

  function clearCompanyFilter() {
    if (unassignedOnly) {
      const next = new URLSearchParams(searchParams);
      next.delete("company");
      setSearchParams(next);
      return;
    }
    setScope({ companyId: "" });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = premises;
    if (unassignedOnly) list = list.filter((p) => !p.companyId);
    else if (scope.companyId) {
      list = list.filter((p) => p.companyId === scope.companyId);
    }
    if (scope.product) {
      list = list.filter((p) =>
        productsForPremise(p).has(scope.product as ProductKey)
      );
    }
    if (q) {
      list = list.filter(
        (p) =>
          p.premiseName.toLowerCase().includes(q) ||
          p.premiseId.toLowerCase().includes(q) ||
          (p.models ?? []).some((m) => m.toLowerCase().includes(q))
      );
    }
    if (typeOrder) {
      const dir = typeOrder === "asc" ? 1 : -1;
      const key = (x: PremiseSummary) =>
        premiseTypeChips(x)
          .map((c) => c.label)
          .join(", ");
      list = [...list].sort(
        (a, b) =>
          key(a).localeCompare(key(b)) * dir ||
          a.premiseName.localeCompare(b.premiseName)
      );
    }
    return list;
    // NB: scope.premiseId deliberately does NOT filter here. This list is
    // where you pick a location - narrowing it to the one already picked
    // leaves you a one-row table and no way back out.
  }, [
    premises,
    query,
    unassignedOnly,
    scope.companyId,
    scope.product,
    typeOrder,
  ]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Properties"
        title="Premises & Screens"
        actions={
          <>
            {/* The customer narrowing lives in the header's Customer selector
                now - a second chip for it just crowded the same row. Only
                "unassigned" still needs one: the selector cannot express it. */}
            {unassignedOnly && (
              <Chip
                label="Unassigned"
                onDelete={clearCompanyFilter}
                deleteIcon={<ClearIcon />}
                variant="outlined"
                data-testid="premises-company-filter"
              />
            )}
            {canCreate && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                data-testid="premises-new"
              >
                New premise
              </Button>
            )}
          </>
        }
      />
      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search premises by name, ID, or TV model"
        size="small"
        fullWidth
        inputProps={{ "data-testid": "premise-search" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {loading && <TableSkeleton rows={6} />}
      {error && (
        <Typography color="error" data-testid="premises-error">
          {error.message}
        </Typography>
      )}
      {!loading && !error && filtered.length === 0 && (
        <Typography color="text.secondary">
          {query
            ? `No premises matching "${query}".`
            : "No premises yet. Run npm run seed to populate demo data."}
        </Typography>
      )}
      {filtered.length > 0 && (
        <TableContainer>
          <Table size="small" data-testid="premises-list">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sortDirection={typeOrder ?? false}>
                  <TableSortLabel
                    active={typeOrder !== null}
                    direction={typeOrder ?? "asc"}
                    onClick={() =>
                      setTypeOrder((o) => (o === "asc" ? "desc" : "asc"))
                    }
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell align="right">Rooms</TableCell>
                <TableCell>Health</TableCell>
                <TableCell align="right">Online</TableCell>
                <TableCell align="right">Licenses</TableCell>
                <TableCell>Displays</TableCell>
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.premiseId}
                  hover
                  onClick={() => navigate(`/premises/${p.premiseId}`)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`premise-${p.premiseId}`}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {p.premiseName}
                    </Typography>
                  </TableCell>
                  <TableCell data-testid={`premise-${p.premiseId}-segment`}>
                    {premiseTypeChips(p).length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        none
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        {premiseTypeChips(p).map((c) => (
                          <Chip
                            key={c.label}
                            size="small"
                            label={c.label}
                            color="info"
                            variant={c.kind === "addon" ? "outlined" : "filled"}
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {p.premiseId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{p.roomCount}</Typography>
                  </TableCell>
                  <TableCell>
                    <HealthChip
                      health={p.health}
                      testId={`premise-${p.premiseId}-health`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      color={p.onlineCount > 0 ? "success" : "default"}
                      label={`${p.onlineCount}/${p.screenCount}`}
                      data-testid={`premise-${p.premiseId}-online`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {p.licenses ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            p.licenses.unlimited
                              ? "success"
                              : p.licenses.hasFreeSeats
                                ? "primary"
                                : "error"
                          }
                          label={
                            p.licenses.unlimited
                              ? `${p.licenses.activatedCount} ∞`
                              : `${p.licenses.activatedCount}/${p.licenses.totalSeats}`
                          }
                          data-testid={`premise-${p.premiseId}-licenses`}
                        />
                        {p.licenses.demoCount > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`+${p.licenses.demoCount} demo`}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {/* `?? []` for the same reason as the overview cards: a
                          summary row that arrives without `models` must render
                          as "none", not throw and blank the whole page. */}
                      {(p.models ?? []).length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          none
                        </Typography>
                      ) : (
                        // Vendors, not model strings: the same rollup the
                        // premise overview shows, and it fits on one line.
                        [...new Set((p.models ?? []).map(vendorFor))].map(
                          (v) => (
                            <Tooltip
                              key={v}
                              title={(p.models ?? [])
                                .filter((m) => vendorFor(m) === v)
                                .join(", ")}
                            >
                              <Chip size="small" variant="outlined" label={v} />
                            </Tooltip>
                          )
                        )
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {canDelete && (
                      <Tooltip title="Delete premise">
                        <IconButton
                          size="small"
                          aria-label={`Delete ${p.premiseName}`}
                          onClick={() => {
                            setDeleteErr(null);
                            setDeleting(p);
                          }}
                          data-testid={`premise-delete-${p.premiseId}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ height: 24 }} />

      <CreatePremiseDialog
        open={createOpen}
        companies={companies}
        defaultCompanyId={unassignedOnly ? "" : scope.companyId}
        onClose={() => setCreateOpen(false)}
        onCreated={(slug) => {
          setCreateOpen(false);
          setToast({ msg: "Premise created", severity: "success" });
          refresh();
          navigate(`/premises/${slug}`);
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
      />

      <DangerConfirmDialog
        open={!!deleting}
        title="Delete premise"
        entityName={
          deleting ? `${deleting.premiseName} (${deleting.premiseId})` : ""
        }
        body={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This permanently removes the premise and everything attached to
              it:
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                paddingLeft: 18,
                color: "text.secondary",
                fontSize: 13,
              }}
            >
              <li>
                {deleting?.roomCount ?? 0} room
                {deleting?.roomCount === 1 ? "" : "s"}
              </li>
              <li>
                {deleting?.screenCount ?? 0} paired screen
                {deleting?.screenCount === 1 ? "" : "s"} (unpaired)
              </li>
              <li>
                All licenses ({deleting?.licenses?.totalSeats ?? 0} seat
                {deleting?.licenses?.totalSeats === 1 ? "" : "s"})
              </li>
              <li>Pairing codes and screen telemetry</li>
            </Box>
          </Box>
        }
        errorMessage={deleteErr}
        busy={deleteBusy}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleting(null);
          setDeleteErr(null);
        }}
        onConfirm={async () => {
          if (!deleting) return;
          setDeleteBusy(true);
          setDeleteErr(null);
          try {
            await foyerApi.deletePremise(deleting.premiseId);
            setToast({
              msg: `Deleted ${deleting.premiseName}`,
              severity: "success",
            });
            setDeleting(null);
            refresh();
          } catch (e) {
            setDeleteErr(e instanceof Error ? e.message : String(e));
          } finally {
            setDeleteBusy(false);
          }
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity}>{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
}

/** Client-side slug preview; the server validates + owns the final value. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CreatePremiseDialog({
  open,
  companies,
  defaultCompanyId,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  companies: CompanySummary[];
  defaultCompanyId: string;
  onClose: () => void;
  onCreated: (slug: string) => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setSlugError(null);
      setCompanyId(defaultCompanyId === "unassigned" ? "" : defaultCompanyId);
    }
  }, [open, defaultCompanyId]);

  // The slug auto-tracks the name until the user edits it directly.
  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid =
    effectiveSlug.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(effectiveSlug);

  async function submit() {
    setBusy(true);
    setSlugError(null);
    try {
      const created = await foyerApi.createPremise({
        name,
        slug: effectiveSlug,
        companyId,
      });
      onCreated(created.premiseId);
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === "slug_already_exists") {
        setSlugError(
          `“${effectiveSlug}” is already taken - choose a different premise ID.`
        );
      } else if (code === "invalid_slug") {
        setSlugError(
          "Invalid premise ID - use lowercase letters, numbers and hyphens."
        );
      } else {
        onError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New premise</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            size="small"
            helperText="Human-friendly name, e.g. “Hotel Hasselbacken”."
            inputProps={{ "data-testid": "premise-name" }}
          />
          <TextField
            label="Premise ID (slug)"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
              setSlugError(null);
            }}
            size="small"
            error={(!!effectiveSlug && !slugValid) || !!slugError}
            helperText={
              slugError
                ? slugError
                : !!effectiveSlug && !slugValid
                  ? "Lowercase letters, numbers and hyphens only (must start alphanumeric)."
                  : "Permanent ID used in URLs and device pairing. Auto-filled from the name - edit to set it up."
            }
            inputProps={{
              "data-testid": "premise-slug",
              style: { fontFamily: "ui-monospace, Menlo, monospace" },
            }}
          />
          <TextField
            select
            label="Company"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
            size="small"
            helperText={
              isDemoCompanyName(companies.find((c) => c.id === companyId)?.name)
                ? "Everything under Nenda Demo is a sales demo: no billing, no alerts."
                : "The customer this premise belongs to."
            }
            inputProps={{ "data-testid": "premise-company" }}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {isDemoCompanyName(c.name) ? `${c.name} (sales demo)` : c.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={busy || !name.trim() || !slugValid || !companyId}
          onClick={submit}
          data-testid="premise-create"
        >
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
